// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Hook central de dados via Supabase
// Estratégia: carregar dados críticos primeiro, notas em background
// src/utils/useSupabaseData.js
// ═══════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabaseClient";
import { instalarFeriados, criarFeriadoLookup } from "./negocio";
import { inicioJanela, erroDoPeriodo, hojeSP } from "./janelaNotas";

// ─── Seletores de colunas — buscar só o necessário ────────────────
// Notas: excluir colunas grandes desnecessárias na listagem
const SEL_NOTA = [
  "id","numero","serie","filial","data_emissao","data_embarque",
  "data_iso","cod_cli","loja","municipio","municipio_entrega",
  "uf","uf_entrega","cod_ibge","cod_ibge_entrega","just_mun_entrega","cod_transp_usada","vlr_bruto","peso_bruto",
  "volume","data_entrega_real","status_nf","extras",
  // Alterações manuais por campo (migration 20260813000001_overrides). Sem esta
  // coluna, a troca de transportadora feita na tela sumia no F5.
  "overrides",
  // Agendamento de entrega (migration 20260810000001_agendamento_entrega)
  "data_agendada","just_agendamento","agendado_em","agendado_por",
  // Indicadores já calculados (migration 20260723000003_notas_indicadores).
  // Sem eles, as telas recalculavam prazo/OTIF por conta própria — e cada
  // uma com uma regra diferente. Lendo o valor gravado, todas usam a mesma.
  "no_prazo","status_validacao","previsao_iso"
].join(",");

// Fallback sem colunas IBGE para bancos que ainda não rodaram o ALTER TABLE
const SEL_NOTA_FALLBACK = [
  "id","numero","serie","filial","data_emissao","data_embarque",
  "data_iso","cod_cli","loja","municipio","municipio_entrega",
  "uf","uf_entrega","cod_transp_usada","vlr_bruto","peso_bruto",
  "volume","data_entrega_real","status_nf","extras"
].join(",");

// extras guarda o TDE por transportadora (#85), a observação e a vigência (#90).
// Sem ler aqui, nada disso voltava do banco e a regravação do cliente o apagava.
const SEL_CLI = "id,cod,loja,nome,tipo,localizacao,filial,municipio,uf,cod_ibge,extras";

// ─── Resolver valor ou callback (como React.setState) ────────────
function resolver(novoOuFn, atual) {
  return typeof novoOuFn === "function" ? novoOuFn(atual) : novoOuFn;
}

// ─── Identidade da nota ──────────────────────────────────────────
// ATENÇÃO: as notas em memória vêm de rowParaNota, que grava o id do banco
// em "_id" (não em "id"). Qualquer indexação por id PRECISA usar este helper,
// senão todas as notas caem na mesma chave undefined.
function idNota(n) {
  return n.id || n._id ||
    `${String(n.filial||"00").padStart(2,"0")}-${String(n.numero||"").padStart(9,"0")}-${n.serie||"1"}`;
}

// ─── Tamanho do lote de upsert (adaptativo, por sessão) ──────────
// Começa otimista e só desce. Fica FORA do hook de propósito: a informação
// "este ambiente não aguenta lotes grandes" vale para a sessão inteira, não
// para uma chamada. Sem isso, cada atualização recomeçaria em 20.000,
// desperdiçando ~6MB e alguns segundos antes de descobrir o mesmo de novo.
let loteAtual = 5000;

// ─── Deduplicação antes do upsert ────────────────────────────────
// O Postgres recusa um INSERT ... ON CONFLICT quando o MESMO id aparece duas
// vezes no mesmo comando: "ON CONFLICT DO UPDATE command cannot affect row a
// second time". Como o id de várias entidades é derivado dos dados
// (cliente = cod-loja, nota = nf-numero-serie-filial), uma planilha com a
// linha repetida — ou um cadastro manual que colide com um já importado —
// derruba o lote INTEIRO. Nada é salvo, mas a tela já mostrou o dado porque
// o estado local é atualizado antes da gravação: o registro parece existir
// até o próximo F5.
//
// Mantém a ÚLTIMA ocorrência: em geral é a edição mais recente.
function dedupPorId(rows, rotulo) {
  const m = new Map();
  rows.forEach(r => m.set(r.id, r));
  if (m.size !== rows.length) {
    console.warn(`[${rotulo}] ${rows.length - m.size} registro(s) duplicado(s) ` +
      `descartado(s) antes de salvar (mesmo id).`);
  }
  return [...m.values()];
}

// ─── Nota (formato app) → linha do banco (snake_case) ────────────
function nfParaRow(n) {
  return {
    id: idNota(n),
    numero:n.numero, serie:n.serie, filial:n.filial,
    data_emissao:n.dataEmissao, data_embarque:n.dataEmbarque, data_iso:n.dataISO,
    cod_cli:n.codCli, loja:n.loja,
    municipio:n.municipio, municipio_entrega:n.municipioEntrega,
    uf:n.uf, uf_entrega:n.ufEntrega,
    cod_ibge:n.codIBGE||"", cod_ibge_entrega:n.codIBGEEntrega||"",
    // Justificativa da troca de município de entrega. Faltava aqui: o campo
    // era preenchido pela tela e pela importação de bilhetes e nunca chegava
    // ao banco (migration 20260805000001_just_mun_entrega).
    just_mun_entrega:n.justMunEntrega || null,
    cod_transp_usada:n.codTranspUsada,
    vlr_bruto:n.vlrBruto||0, peso_bruto:n.pesoBruto||0, volume:n.volume||0,
    data_entrega_real:n.dataEntregaReal||null, status_nf:n.statusNF||null,
    // Agendamento — quando preenchido, a data agendada SUBSTITUI a previsão
    // no OTIF. Sem estas quatro linhas o agendamento sumiria no F5, como já
    // aconteceu com historicoTransp, motivoEmbarque e just_mun_entrega.
    data_agendada:n.dataAgendada||null,
    just_agendamento:n.justAgendamento||null,
    agendado_em:n.agendadoEm||null,
    agendado_por:n.agendadoPor||null,
    // extras guarda o histórico de alteração de transportadora. Estava fixo
    // em {} — o import de entregas montava a justificativa "Transportadora
    // alterada (X → Y)" e ela era APAGADA aqui em toda gravação. Quando 301
    // notas tiveram a transportadora reescrita por engano, não sobrou trilha
    // nenhuma para descobrir o dono original. Por isso extras agora é lido
    // (SEL_NOTA) e regravado.
    extras:n.extras||{},
    // ⚠ Histórico de alteração manual por campo. Ficou de fora daqui desde
    // sempre: a tela gravava a troca de transportadora com justificativa, o
    // estado local mostrava certo, e o valor era descartado nesta linha —
    // silenciosamente, porque o upsert não reclama de campo ausente.
    overrides:n.overrides||{},
    // Indicadores calculados no JS e persistidos (ver migration 20260723000003_notas_indicadores)
    prazo_calc:       n.prazoCalc       ?? null,
    previsao_iso:     n.previsaoISO     || null,
    no_prazo:         n.noPrazo         ?? null,
    status_validacao: n.statusValidacao || null,
    calc_em:          n.calcEm          || null,
  };
}

// Campos de indicadores — removidos do payload se as colunas ainda não existirem
// Colunas de agendamento (migration 20260810000001_agendamento_entrega). Mesmo
// tratamento das de indicador: banco sem a migration não pode DERRUBAR a
// gravação inteira. Um campo novo bloqueando a importação de entregas é
// desproporcional — o resto da nota continua válido.
const COLS_AGENDA = ["data_agendada","just_agendamento","agendado_em","agendado_por",
                     // overrides entra no mesmo grupo de degradação: banco sem
                     // a migration 20260813 não pode derrubar a gravação toda.
                     "overrides"];
function semAgenda(row) {
  const r = { ...row };
  COLS_AGENDA.forEach(c => delete r[c]);
  return r;
}

const COLS_INDICADORES = ["prazo_calc","previsao_iso","no_prazo","status_validacao","calc_em"];
function semIndicadores(row) {
  const r = { ...row };
  COLS_INDICADORES.forEach(c => delete r[c]);
  return r;
}

// Payload enxuto: só a chave e as colunas que a atualização de indicadores
// realmente muda. A linha completa tem 27 colunas (número, cliente, município,
// valor, peso, extras…) e nada disso é alterado aqui — medimos 4,6s para
// 5.000 notas, quase tudo transferência inútil.
//
// O upsert vira INSERT ... ON CONFLICT (id) DO UPDATE SET <colunas enviadas>,
// então para linhas EXISTENTES atualiza exatamente estes campos. É o caso por
// construção: só atualizamos notas lidas do banco. Se alguma não existir, o
// INSERT falharia por falta de numero/filial — daí o fallback para a linha
// completa em atualizarNotasParcial.
function soIndicadores(row) {
  const r = { id: row.id };
  COLS_INDICADORES.forEach(c => { if (c in row) r[c] = row[c]; });
  return r;
}

// ─── Wrapper de segurança: garante que erros NUNCA fiquem silenciosos ──
// Como os módulos chamam setTabelas(...) sem await (fire-and-forget),
// qualquer erro lançado dentro se torna uma "unhandled promise rejection"
// que o browser ignora silenciosamente. Este wrapper captura e expõe.
function comTratamentoErro(fn, setErroSalvar, setSalvando, chave, setState) {
  return (...args) => {
    // Foto do estado ANTES: os setters atualizam a tela primeiro e gravam
    // depois. Sem desfazer, um upsert que falha deixa na tela — e no cache —
    // um valor que o banco não tem, e o usuário só descobre no dia seguinte.
    // Otimismo só se paga com volta garantida (#101).
    const anterior = chave ? cache[chave] : undefined;
    setSalvando(true);
    const promise = fn(...args);
    promise
      .then(() => { setSalvando(false); })
      .catch(err => {
        console.error("🔴 ERRO AO SALVAR (não propagado para a tela):", err);
        if (chave) { cache[chave] = anterior; setState?.(anterior); }
        setErroSalvar(`Falha ao salvar: ${err.message || err}`);
        setSalvando(false);
      });
    return promise;
  };
}

// ─── Converters row → objeto ──────────────────────────────────────
function rowParaNota(r) {
  return {
    // id E _id: o app inteiro indexa nota por "id" (chaves de lista, updates
    // pontuais como registrarMunEntrega/limparEntrega). Expondo só "_id", todo
    // n.id ficava undefined — quebrava o backfill e fazia updates casarem com
    // todas as notas de uma vez.
    id: r.id, _id: r.id, numero: r.numero, serie: r.serie, filial: r.filial,
    dataEmissao: r.data_emissao, dataEmbarque: r.data_embarque, dataISO: r.data_iso,
    codCli: r.cod_cli, loja: r.loja,
    municipio: r.municipio, municipioEntrega: r.municipio_entrega,
    uf: r.uf, ufEntrega: r.uf_entrega,
    codIBGE: r.cod_ibge||"", codIBGEEntrega: r.cod_ibge_entrega||"",
    justMunEntrega: r.just_mun_entrega || "",
    codTranspUsada: r.cod_transp_usada,
    vlrBruto: r.vlr_bruto||0, pesoBruto: r.peso_bruto||0, volume: r.volume||0,
    dataEntregaReal: r.data_entrega_real||null, statusNF: r.status_nf||null,
    overrides: r.overrides || {},
    dataAgendada: r.data_agendada||null, justAgendamento: r.just_agendamento||null,
    agendadoEm: r.agendado_em||null,     agendadoPor: r.agendado_por||null,
    // Indicadores gravados pelo recálculo automático (NotasFiscais).
    // noPrazo é tri-estado de propósito: true = entregue no prazo,
    // false = entregue com atraso, null = ainda não avaliada (sem entrega
    // ou sem cálculo). Quem for medir OTIF precisa ignorar os null, não
    // tratá-los como atraso.
    noPrazo: r.no_prazo ?? null,
    statusValidacao: r.status_validacao || null,
    previsaoISO: r.previsao_iso || null,
    // Sem ler extras aqui, regravar a nota apagaria o histórico do banco.
    extras: r.extras || {},
  };
}
function rowParaCliente(r) {
  // id E _id, como rowParaNota e rowParaMunicipio. A tela de Clientes indexa
  // por "id": chave de lista, toggle do detalhe e o filtro do excluir. Só com
  // "_id", todo c.id ficava undefined — o detalhe nunca abria e o excluir,
  // comparando undefined com undefined, não guardava nenhuma linha (#96).
  return { _id:r.id, id:r.id, cod:r.cod, loja:r.loja, nome:r.nome,
           tipo:r.tipo, localizacao:r.localizacao, filial:r.filial,
           municipio:r.municipio||"", uf:r.uf||"", codIBGE:r.cod_ibge||"",
           extras:r.extras||{}, tde:r.extras?.tde||{},
           obs:r.extras?.obs||"", vigencia:r.extras?.vigencia||"",
           // CNPJ vive em extras: a importação lia e a tela mostrava, mas
           // nada gravava — sumia no F5 e a busca por CNPJ não achava (#320).
           cnpj:r.extras?.cnpj||"",
           // Funcionário da empresa: pessoa física, entrega residencial,
           // atraso esperado. Fica fora dos indicadores de prazo (#107).
           funcionario:!!r.extras?.funcionario };
}
// ─── Objeto do app → linha do banco ───────────────────────────────
// Ficavam embutidos dentro dos setters. Saíram para o caminho completo
// (importação) e o parcial (edição de um registro) usarem a MESMA conversão:
// duas versões divergem na primeira correção que entra só em uma (#103).
function clienteParaRow(c) {
  return {
    id:c._id||`${c.cod||""}-${c.loja||""}`,
    cod:c.cod, loja:c.loja, nome:c.nome,
    tipo:c.tipo, localizacao:c.localizacao, filial:c.filial,
    municipio:c.municipio||"", uf:c.uf||"", cod_ibge:c.codIBGE||"",
    // Era extras:{} fixo: o TDE editado ou importado em Clientes nunca
    // chegava ao banco (#85). Observação e vigência também não (#90).
    // Preserva o resto de extras.
    extras:{ ...(c.extras||{}), tde:c.tde||{}, obs:c.obs||"", vigencia:c.vigencia||"",
             cnpj:c.cnpj||"",
             funcionario:!!c.funcionario },
  };
}

function ocorrenciaParaRow(o) {
  return {
    id:o.id, titulo:o.titulo, tipo:o.tipo, status:o.status,
    prioridade:o.prioridade, nf:o.nf, filial:o.filial,
    cod_cliente:o.codCliente, nome_cliente:o.nomeCliente,
    data_abertura_iso:o.dataAberturaISO, obs:o.obs,
    atendimento:o.atendimento, operador:o.operador,
    nome_operador:o.nomeOperador, cod_vendedor:o.codVendedor,
    nome_vendedor:o.nomeVendedor,
  };
}

function rowParaTabela(r) {
  return {
    id:r.id, cod:r.cod, codTransp:r.cod_transp||"", transp:r.transp, tipo:r.tipo, modal:r.modal,
    vigIni:r.vig_ini, vigFim:r.vig_fim,
    adv:r.adv, advMin:r.adv_min, pgv:r.pgv, rkg:r.rkg, pesoAcima:+r.peso_acima||0, rkgAcima:+r.rkg_acima||0,
    minimo:r.minimo, gris:r.gris, grisMin:r.gris_min,
    desp:r.desp, tas:r.tas, tad:r.tad, ped:r.ped, pedMin:r.ped_min||0,
    rdc:r.rdc, rdcTipo:r.rdc_tipo||'percNota', icms:r.icms, excedente:r.excedente, frac:r.frac,
    rotas:r.rotas||[], faixas:r.faixas||[],
  };
}
function rowParaMunicipio(r) {
  return {
    id:r.id, mun:r.mun, uf:r.uf, filial:r.filial, codIBGE:r.cod_ibge||"",
    filialPadrao:r.filial_padrao, transpPadrao:r.transp_padrao,
    prazoPadrao:r.prazo_padrao, transpMec:r.transp_mec, prazoMec:r.prazo_mec,
    transpRural:r.transp_rural, prazoRural:r.prazo_rural,
    transpCWB:r.transp_cwb, transpMGA:r.transp_mga, transpCVL:r.transp_cvl,
    transpRBP:r.transp_rbp, transpMRL:r.transp_mrl,
    // Prazo por filial de saída (migration 20260729000003_prazo_por_filial).
    // null = não informado → cai no prazoPadrao.
    prazoCWB:r.prazo_cwb ?? null, prazoMGA:r.prazo_mga ?? null,
    prazoCVL:r.prazo_cvl ?? null, prazoRBP:r.prazo_rbp ?? null,
    prazoMRL:r.prazo_mrl ?? null,
    temTDE:r.tem_tde, taxas:r.taxas||[], obs:r.obs||"",
  };
}
function rowParaOcorrencia(r) {
  return {
    id:r.id, titulo:r.titulo, tipo:r.tipo, status:r.status,
    prioridade:r.prioridade, nf:r.nf, filial:r.filial,
    codCliente:r.cod_cliente, nomeCliente:r.nome_cliente,
    dataAberturaISO:r.data_abertura_iso, obs:r.obs,
    atendimento:r.atendimento, operador:r.operador,
    nomeOperador:r.nome_operador, codVendedor:r.cod_vendedor,
    nomeVendedor:r.nome_vendedor,
  };
}

function rowParaTransportadora(r) {
  return {
    id:r.id, cod:r.cod, nome:r.nome, short:r.short||r.nome,
    filial:r.filial||"", ativo:r.ativo !== false,
    // Site de rastreio do robô (#326, #331). Sem ler aqui, salvar o cadastro desmarcava.
    rastreio:r.rastreio || "",
    // Sigla que o SSW mostra na situação, para o robô saber quem tem a nota (#336).
    siglaSsw:r.sigla_ssw || "",
    // CNPJs que emitem CT-e por esta transportadora, e frota própria (#342).
    cnpjs:r.cnpjs || [], frotaPropria:r.frota_propria === true,
    // CT-e de subcontratação em que a Lubpress contratou (ex.: FAT → 100201).
    cnpjsViaLubpress:r.cnpjs_via_lubpress || [],
  };
}

// ─── Busca direcionada de NFs por número ─────────────────────────
// A Auditoria de Fatura precisa casar cada linha da fatura com a NF
// correspondente. Antes ela varria a base inteira em memória, o que obrigava
// carregar as 70k notas no boot. Aqui buscamos SÓ as notas citadas na fatura
// — algumas centenas — direto no banco, usando o índice de "numero".
export async function buscarNotasPorNumeros(numeros) {
  const base = (numeros || []).map(n => String(n || "").trim()).filter(Boolean);
  if (!base.length) return [];
  // O número pode estar gravado com ou sem zeros à esquerda — busca as variações
  const variantes = new Set();
  base.forEach(n => {
    const semZero = n.replace(/^0+/, "");
    variantes.add(n);
    if (semZero) { variantes.add(semZero); variantes.add(semZero.padStart(9, "0")); }
  });
  const lista = [...variantes].filter(Boolean);
  const out = [];
  // Lotes de 150: com muitos valores o .in() gera uma URL longa demais (400)
  for (let i = 0; i < lista.length; i += 150) {
    const { data, error } = await supabase.from("notas_fiscais")
      .select(SEL_NOTA).in("numero", lista.slice(i, i + 150));
    if (error) throw error;
    (data || []).forEach(r => out.push(rowParaNota(r)));
  }
  // Dedupe por id (as variações podem trazer a mesma linha)
  const porId = new Map();
  out.forEach(n => porId.set(idNota(n), n));
  return [...porId.values()];
}

// ─── Cache em memória entre re-renders ───────────────────────────
const cache = { notas: null, desde: null, clientes: null, tabelas: null,
                municipios: null, ocorrencias: null, transportadoras: null };

// ─── Hook principal ──────────────────────────────────────────────
export function useSupabaseData(enabled = true) {
  const [notas,           setNotasState]          = useState(cache.notas          || []);
  const [clientes,        setClientesState]       = useState(cache.clientes       || []);
  const [tabelas,         setTabelasState]        = useState(cache.tabelas        || []);
  const [municipios,      setMunicipiosState]     = useState(cache.municipios     || []);
  const [ocorrencias,     setOcorrenciasState]    = useState(cache.ocorrencias    || []);
  const [transportadoras, setTransportadorasState]= useState(cache.transportadoras|| []);

  // Fase 1: críticos (tabelas + municípios) — interface disponível em ~1s
  const [loadingCriticos, setLoadingCriticos] = useState(!cache.tabelas);
  // Fase 2: volumosos (notas + clientes) — carregam em background
  const [loadingNotas,    setLoadingNotas]    = useState(!cache.notas);
  // Data (AAAA-MM-DD) a partir da qual as notas emitidas estão carregadas (#400)
  const [notasDesde,      setNotasDesde]      = useState(cache.desde);
  const [loadingClientes, setLoadingClientes] = useState(!cache.clientes);
  const [progresso,       setProgresso]       = useState("");
  const [auditoriaFretes, setAuditoriaFretesState] = useState(cache.auditoriaFretes || []);
  const [auditorias, setAuditoriasState] = useState(cache.auditorias || []);
  const [rastreioSsw, setRastreioSswState] = useState(cache.rastreioSsw || []);
  const [feriados, setFeriadosState] = useState(cache.feriados || []);
  // KPIs agregados no banco (views por mês) — algumas dezenas de linhas
  const [kpis, setKpisState] = useState(cache.kpis || { gerais:[], transp:[], municipio:[] });
  const [erro,            setErro]            = useState(null);
  const [erroSalvar,      setErroSalvar]      = useState(null);
  const [salvando,        setSalvando]        = useState(false);
  const iniciado = useRef(false);

  useEffect(() => {
    if (!enabled) {
      // Contexto Lubpress: este hook não carrega nada.
      // Sem isso, os estados de loading ficam true para sempre e travam a tela.
      setLoadingCriticos(false);
      setLoadingNotas(false);
      setLoadingClientes(false);
      return;
    }
    if (iniciado.current) return;
    iniciado.current = true;
    if (cache.tabelas) {
      // Cache em memória — restaurar estados sem ir ao Supabase
      setTabelasState(cache.tabelas);
      setMunicipiosState(cache.municipios || []);
      setOcorrenciasState(cache.ocorrencias || []);
      setClientesState(cache.clientes || []);
      setNotasState(cache.notas || []);
      setLoadingCriticos(false);
      setLoadingNotas(!cache.notas);
      setLoadingClientes(!cache.clientes);
      return;
    }
    // Começo do carregamento real. Reafirma os flags como TRUE — quando o hook
    // estava desabilitado antes (fluxo Login → seleção de grupo), o branch
    // !enabled havia zerado esses flags, e sem isso a tela de loading não
    // segurava até notas/clientes terminarem (dashboard abria "enchendo" ao vivo).
    setLoadingCriticos(true);
    setLoadingNotas(true);
    setLoadingClientes(true);
    carregarFase1();
  }, [enabled]);

  // ── FASE 1: tabelas + municípios + ocorrências (rápido ~1-2s) ──
  async function carregarFase1() {
    try {
      const [resTab, resMun, resOc, resTr] = await Promise.all([
        supabase.from("tabelas_frete").select("*").order("cod"),
        supabase.from("municipios").select("*").order("mun"),
        supabase.from("ocorrencias").select("*")
          .order("data_abertura_iso", { ascending:false }).limit(500),
        supabase.from("transportadoras").select("*").order("cod"),
      ]);
      if (resTab.error) throw resTab.error;
      if (resMun.error) throw resMun.error;

      // Guardar updated_at para detectar conflitos depois
      const t  = resTab.data.map(r => ({ ...rowParaTabela(r), _updated_at: r.updated_at }));
      const m  = resMun.data.map(r => ({ ...rowParaMunicipio(r), _updated_at: r.updated_at }));
      const o  = (resOc.data||[]).map(rowParaOcorrencia);
      // Transportadoras: se tabela vazia, usa o array de constantes como fallback
      const tr = (resTr.data||[]).length > 0
        ? resTr.data.map(rowParaTransportadora)
        : [];

      cache.tabelas         = t;
      cache.municipios      = m;
      cache.ocorrencias     = o;
      cache.transportadoras = tr;

      setTabelasState(t);
      setMunicipiosState(m);
      setOcorrenciasState(o);
      setTransportadorasState(tr);
      setLoadingCriticos(false); // ← Interface libera aqui!

      // KPIs agregados: leves e prontos, não dependem das 70k notas
      carregarKpis();

      // Fase 2 em background — NOTAS não entram aqui de propósito.
      // Os KPIs vêm agregados do banco, então a abertura não precisa das 70k
      // notas. Elas são buscadas sob demanda (garantirNotas) pelos módulos
      // que realmente precisam da base linha a linha.
      // auditoria_fretes (~5 MB) só o Mapa Operacional lê: garantirAuditoriaFretes (#178).
      carregarClientes();
      carregarAuditorias();
      carregarRastreioSsw();
      carregarFeriados();
    } catch(e) {
      console.error("Erro carregarFase1:", e.message);
      // Não travar a UI — deixar passar com arrays vazios
      setErro(e.message);
      setLoadingCriticos(false);
      setLoadingNotas(false);
      setLoadingClientes(false);
    }
  }

  // ── KPIs agregados (views) — payload minúsculo, carrega junto da fase 1 ──
  // Resiliente: se as views ainda não existirem, o app segue calculando no
  // cliente (rode as migrations 20260723000003_notas_indicadores + 20260723000004_kpis_mensais).
  async function carregarKpis() {
    try {
      // Matviews refeitas pelo banco a cada 5 min (migration
      // 20260929110246_kpis_materializados, #169): respondem em milissegundos.
      // Se faltarem, cai no aviso abaixo e a tela usa o cálculo local.
      const res = {};
      for (const [nome, view] of [
        ["gerais",    "mv_kpis_gerais"],
        ["transp",    "mv_otif_transportadora"],
        ["municipio", "mv_otif_municipio"],
      ]) {
        const t0 = performance.now();
        const r = await supabase.from(view).select("*");
        res[nome] = { ...r, ms: Math.round(performance.now() - t0) };
      }
      // O supabase-js devolve { data, error } — não lança. Sem imprimir
      // error.code aqui, um 500 da view fica indistinguível de timeout,
      // permissão ou recursão de policy, e a tela só emagrece em silêncio (#143).
      const falhas = Object.entries(res).filter(([, r]) => r.error);
      if (falhas.length) {
        falhas.forEach(([nome, r]) => console.warn(
          `[TMS] view de KPI ${nome} falhou em ${r.ms}ms (${r.error.code || "sem code"}): ${r.error.message}`,
          r.error));
        console.warn("[TMS] Views de KPI indisponíveis — usando cálculo no cliente.");
        return;
      }
      const novo = {
        gerais:    res.gerais.data    || [],
        transp:    res.transp.data    || [],
        municipio: res.municipio.data || [],
      };
      cache.kpis = novo;
      setKpisState(novo);
      // Os tempos são o dado que faltou na #143: medir no SQL Editor deu
      // 10x menos do que produção. São eles que dizem se o teto de 30s pode
      // apertar de volta ou se o caminho é cachear.
      console.log(`⏱ [kpis] agregados: ${novo.gerais.length} gerais · ${novo.transp.length} transp · ${novo.municipio.length} municípios (${res.gerais.ms}+${res.transp.ms}+${res.municipio.ms}ms)`);
    } catch (e) {
      console.warn("[TMS] KPIs:", e.message);
    }
  }

  // ── FASE 2D: feriados (nacional / municipal / filial) ─────────
  // Instala no módulo de negócio assim que chegam. Enquanto não chegam, vale
  // a lista fixa embutida — o cálculo nunca fica pior do que era.
  async function carregarFeriados() {
    try {
      const { data, error } = await supabase.from("feriados")
        .select("*").order("data_iso");
      if (error) {
        console.warn("[TMS] feriados indisponível — rode a migration 20260729000001_feriados.sql");
        return;
      }
      cache.feriados = data || [];
      setFeriadosState(data || []);
      instalarFeriados(criarFeriadoLookup(data || []));
    } catch (e) {
      console.warn("[TMS] feriados:", e.message);
    }
  }

  const setFeriados = useCallback(async (novos) => {
    const rows = dedupPorId((novos || []).map(f => ({
      // O recorte entra no id, incluindo a UF: sem ela, dois feriados
      // estaduais na mesma data (9/7 em SP e outro em PR) gerariam o MESMO id
      // e o upsert descartaria um dos dois. Vale também para municipal —
      // existe CAMPO GRANDE em MS e em SC.
      id: f.id || [
        "f", f.dataISO, f.abrangencia || "nacional",
        f.municipio || f.filial || "",
        f.uf || "",
      ].filter(Boolean).join("-"),
      data_iso:    f.dataISO,
      nome:        f.nome,
      abrangencia: f.abrangencia || "nacional",
      municipio:   f.municipio || null,
      uf:          f.uf || null,
      filial:      f.filial || null,
      obs:         f.obs || null,
    })), "feriados");
    const { error } = await supabase.from("feriados").upsert(rows, { onConflict: "id" });
    if (error) { setErroSalvar("Falha ao salvar feriados: " + error.message); throw error; }
    await carregarFeriados();
    setErroSalvar(null);
    return rows.length;
  }, []);

  const excluirFeriado = useCallback(async (id) => {
    const { error } = await supabase.from("feriados").delete().eq("id", id);
    if (error) { setErroSalvar("Falha ao excluir feriado: " + error.message); throw error; }
    await carregarFeriados();
    setErroSalvar(null);
  }, []);

  // ── FASE 2C: fretes auditados (valores pagos) em background ───
  // Resiliente: se a tabela ainda não existir, segue sem quebrar o app
  // (rode a migration 20260723000001_auditoria_fretes.sql).
  async function carregarAuditoriaFretes() {
    try {
      const { data, error } = await supabase.from("auditoria_fretes")
        .select("*").order("data_ref", { ascending: false }).limit(50000);
      if (error) {
        console.warn("[TMS] auditoria_fretes indisponível — rode a migration 20260723000001_auditoria_fretes.sql");
        return;
      }
      cache.auditoriaFretes = data || [];
      setAuditoriaFretesState(data || []);
    } catch (e) {
      console.warn("[TMS] auditoria_fretes:", e.message);
    }
  }

  // ── FASE 2C: histórico de conferência (#141) ──────────────────
  // Antes vivia no localStorage de quem rodava, e por isso o administrador
  // nunca via a conferência do Geovane. Resiliente como as irmãs: sem a
  // tabela, o app segue — a tela só mostra histórico vazio.
  async function carregarAuditorias() {
    try {
      const { data, error } = await supabase.from("auditorias")
        .select("*").order("criado_em", { ascending: false }).limit(200);
      if (error) {
        console.warn("[TMS] auditorias indisponível — rode a migration 20260921000001_auditorias_do_time.sql");
        return;
      }
      cache.auditorias = data || [];
      setAuditoriasState(data || []);
    } catch (e) {
      console.warn("[TMS] auditorias:", e.message);
    }
  }

  // ── Rastreio SSW (#326) ───────────────────────────────────────
  // O que o robô achou no SSW: entregas a aplicar e alertas. Só leitura —
  // quem grava é a Edge Function rastreio-ssw. Sem a tabela, o app segue.
  async function carregarRastreioSsw() {
    try {
      // ponytail: só o que o robô tocou nos últimos 100 dias (a janela dele é
      // de 90). Histórico de nota mais velha, se um dia for preciso, busca
      // por nota.
      const desde = new Date(Date.now() - 100 * 86400000).toISOString();
      // Em páginas pela PK, como clientes: passa do Max Rows (25.000) desde a
      // SimpliRoute e a recuperação desde janeiro (#365), e numa consulta só
      // a tela recebia parte das linhas — e não baixava o resto.
      const PAGE = 20000;
      let todas = [], ultimo = null, continuar = true;
      while (continuar) {
        let q = supabase.from("rastreio_ssw").select("*")
          .gte("consultado_em", desde).order("nota_id", { ascending: true }).limit(PAGE);
        if (ultimo) q = q.gt("nota_id", ultimo);
        const { data, error } = await q;
        if (error) {
          console.warn("[TMS] rastreio_ssw indisponível — rode a migration 20261007114646_rastreio_ssw.sql");
          return;
        }
        todas = todas.concat(data);
        continuar = data.length === PAGE;
        if (data.length) ultimo = data[data.length - 1].nota_id;
      }
      cache.rastreioSsw = todas;
      setRastreioSswState(todas);
    } catch (e) {
      console.warn("[TMS] rastreio_ssw:", e.message);
    }
  }

  const salvarAuditoria = useCallback(async (linha) => {
    const { error } = await supabase.from("auditorias").upsert(linha, { onConflict: "id" });
    if (error) { setErroSalvar("Falha ao salvar a conferência: " + error.message); throw error; }
    await carregarAuditorias();
    setErroSalvar(null);
  }, []);

  const excluirAuditoria = useCallback(async (id) => {
    const { error } = await supabase.from("auditorias").delete().eq("id", id);
    if (error) { setErroSalvar("Falha ao excluir a conferência: " + error.message); throw error; }
    await carregarAuditorias();
    setErroSalvar(null);
  }, []);

  // ── FASE 2A: clientes em background ───────────────────────────
  async function carregarClientes() {
    try {
      // Página grande demais faz uma única resposta de dezenas de MB, que pode
      // falhar/estourar no browser. 25k por requisição mantém poucas idas ao
      // servidor sem gerar um payload gigante. (Max Rows do Supabase >= 25000.)
      const PAGE = 25000;
      // Keyset pela PK (mesma estratégia das notas): sem count exato e sem
      // OFFSET, que juntos estouravam o statement_timeout do Postgres.
      // A ordenação por nome é feita no cliente depois de carregar.
      let todos = [], ultimoId = null, continuar = true;
      while (continuar) {
        let q = supabase.from("clientes").select(SEL_CLI)
          .order("id", { ascending: true }).limit(PAGE);
        if (ultimoId) q = q.gt("id", ultimoId);
        const { data, error } = await q;
        if (error) throw error;
        todos = todos.concat(data.map(rowParaCliente));
        continuar = data.length === PAGE;
        if (data.length) ultimoId = data[data.length - 1].id;
        if (continuar) setProgresso(`Clientes: ${todos.length.toLocaleString("pt-BR")}...`);
      }
      todos.sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || "")));
      cache.clientes = todos;
      setClientesState(todos);
    } catch(e) {
      console.error("Erro clientes:", e.message);
    }
    setLoadingClientes(false);
    setProgresso(p => p.startsWith("Clientes") ? "" : p);
  }

  // ── FASE 2B: notas em background com paginação ─────────────────
  // Resiliente: se colunas novas (cod_ibge, cod_ibge_entrega) não existirem
  // no banco ainda, tenta novamente com o SELECT sem essas colunas.
  async function carregarNotas(selectOverride) {
    const sel = selectOverride || SEL_NOTA;
    try {
      // ── Paginação por CHAVE (keyset), não por OFFSET ──────────────
      // O `range()` do PostgREST vira OFFSET/LIMIT: para buscar a 3ª página
      // o banco precisa percorrer e jogar fora as 50k linhas anteriores, a
      // cada requisição. Somado ao count exato (varredura completa da tabela),
      // isso estourava o statement_timeout do Postgres — o erro
      // "canceling statement due to statement timeout" que apareceu no console.
      //
      // Com keyset, cada página é um seek no índice da PK ("me dê os próximos
      // N depois deste id"): custo constante, independente do tamanho da base.
      // ⚠ Só se a policy de notas_fiscais for `TO authenticated`: com
      // auth.role() na expressão o planejador estimava 478 linhas e trocava o
      // índice por Seq Scan + sort em disco da tabela inteira (#176).
      // O tempo é dominado pela TRANSFERÊNCIA, não pelo número de requisições.
      // Medido com 70.636 notas:
      //     10.000 x 8 req  -> 10.394 ms
      //     50.000 x 2 req  ->  9.720 ms
      //      7.500 x 10 req ->  8.955 ms
      //     20.000 x 4 req  ->  7.571 ms   ← escolhido até a #396
      // 25.000 por página desde a #398 (a base agora entra só com 6 meses); o
      // log "⏱ [notas]" abaixo mostra o tempo real de cada carga.
      // O total varia pouco entre as opções, então o tamanho da página é
      // escolhido principalmente pelo que ele afeta de fato: QUANDO os dados
      // aparecem na tela. Cada bloco é entregue assim que chega
      // (setNotasState abaixo), então a lista fica utilizável antes de a
      // carga inteira terminar. 20k = ~4 blocos, bom equilíbrio entre
      // primeiro lote rápido e poucas idas ao servidor.
      const PAGE = 25000;
      const tFetch = performance.now();
      let todas = [], ultimoId = null, continuar = true, msMap = 0, req = 0;
      // Só a janela recente (#396, #400); o resto vem por carregarPeriodo.
      const desde = inicioJanela(hojeSP());

      while (continuar) {
        let q = supabase.from("notas_fiscais").select(sel).gte("data_iso", desde)
          .order("id", { ascending: true }).limit(PAGE);
        if (ultimoId) q = q.gt("id", ultimoId);
        const { data, error } = await q;
        req++;
        if (error) {
          // Detecta erro de coluna inexistente e tenta sem as colunas novas
          const msg = error.message || "";
          // Degradação em DOIS degraus. Antes havia só o fallback grande, que
          // derruba junto as colunas de IBGE e de indicador — uma coluna nova
          // faltando custava recursos que existem. Se o erro cita uma coluna
          // de agendamento, tira só essas quatro.
          if (!selectOverride && COLS_AGENDA.some(c => msg.includes(c))) {
            console.warn("[TMS] Colunas de agendamento ausentes — rode a migration " +
                         "20260810000001_agendamento_entrega.sql. Carregando sem elas.");
            return carregarNotas(SEL_NOTA.split(",")
              .filter(c => !COLS_AGENDA.includes(c)).join(","));
          }
          if (!selectOverride && (msg.includes("cod_ibge") || msg.includes("does not exist") || msg.includes("column"))) {
            console.warn("[TMS] Colunas IBGE ausentes em notas_fiscais — tentando sem elas. Rode: ALTER TABLE notas_fiscais ADD COLUMN IF NOT EXISTS cod_ibge text; ADD COLUMN IF NOT EXISTS cod_ibge_entrega text;");
            return carregarNotas(SEL_NOTA_FALLBACK);
          }
          throw error;
        }
        const tMap = performance.now();
        todas = todas.concat(data.map(rowParaNota));
        msMap += performance.now() - tMap;
        // Entrega parcial: a tela já mostra o que chegou enquanto o resto vem.
        // O CACHE só é gravado no fim — se fosse preenchido aqui, o
        // garantirNotas() acharia que a carga terminou e devolveria uma base
        // incompleta para quem abrisse outra tela no meio do caminho.
        setNotasState(todas.slice());
        continuar = data.length === PAGE;
        if (data.length) ultimoId = data[data.length - 1].id;
        setProgresso(`Notas: ${todas.length.toLocaleString("pt-BR")}...`);
      }

      const msTotal = Math.round(performance.now() - tFetch);
      console.log(`⏱ [notas] ${todas.length} registros · ${req} req (keyset) · total: ${msTotal}ms · montagem JS: ${Math.round(msMap)}ms`);

      cache.notas = todas;
      cache.desde = desde;
      setNotasDesde(desde);
      setNotasState(todas);
      setErro(null);
    } catch(e) {
      console.error("Erro notas:", e.message);
      // Erro visível ao usuário — não silencioso
      setErro(`Falha ao carregar notas: ${e.message}. Clique em "Recarregar" para tentar novamente.`);
    }
    setLoadingNotas(false);
    setProgresso("");
  }

  // ── Notas antigas, sob demanda (#396, #400) ────────────────────
  // buscarIntervalo lê as notas emitidas de `de` a `ate`; somarNotas as põe na
  // tela sem repetir e devolve quantas são novas.
  async function buscarIntervalo(de, ate) {
    const PAGE = 25000;
    let achadas = [], ultimoId = null;
    for (let mais = true; mais;) {
      let q = supabase.from("notas_fiscais").select(SEL_NOTA)
        .gte("data_iso", de).lte("data_iso", ate).order("id", { ascending: true }).limit(PAGE);
      if (ultimoId) q = q.gt("id", ultimoId);
      const { data, error } = await q;
      if (error) throw error;
      achadas = achadas.concat(data.map(rowParaNota));
      mais = data.length === PAGE;
      if (data.length) ultimoId = data[data.length - 1].id;
    }
    return achadas;
  }
  function somarNotas(achadas) {
    const jaTem = new Set((cache.notas || []).map(idNota));
    const novas = achadas.filter(n => !jaTem.has(idNota(n)));
    if (novas.length) {
      cache.notas = (cache.notas || []).concat(novas);
      setNotasState(cache.notas);
    }
    return novas.length;
  }

  // Um período livre, até 30 dias por vez (pop-up da tela Notas).
  const carregarPeriodo = useCallback(async (de, ate) => {
    const invalido = erroDoPeriodo(de, ate);
    if (invalido) throw new Error(invalido);
    return somarNotas(await buscarIntervalo(de, ate));
  }, []);

  // ── Carregamento sob demanda das notas ──────────────────────────
  // Chamado pelos módulos que precisam da base linha a linha. É idempotente:
  // várias telas podem pedir ao mesmo tempo que a busca acontece uma só vez.
  const notasPedidas = useRef(null);
  const garantirNotas = useCallback(() => {
    if (cache.notas) return Promise.resolve(cache.notas);
    if (notasPedidas.current) return notasPedidas.current;   // já em andamento
    setLoadingNotas(true);
    notasPedidas.current = carregarNotas()
      .finally(() => { notasPedidas.current = null; });
    return notasPedidas.current;
  }, []);

  // Mesmo padrão, para o único leitor de auditoria_fretes: o Mapa Operacional.
  const fretesPedidos = useRef(null);
  const garantirAuditoriaFretes = useCallback(() => {
    if (cache.auditoriaFretes || fretesPedidos.current) return;
    fretesPedidos.current = carregarAuditoriaFretes()
      .finally(() => { fretesPedidos.current = null; });
  }, []);

  // ── Setters com sync para Supabase ────────────────────────────
  const setNotas = useCallback(async (novasOuFn) => {
    console.log("🔵 [setNotas] INICIANDO — recebido:", typeof novasOuFn, Array.isArray(novasOuFn) ? novasOuFn.length+" itens" : novasOuFn);
    const novas = resolver(novasOuFn, cache.notas || []);
    console.log("🔵 [setNotas] resolvido para array de", novas.length, "notas");

    if (novas.length === 0) {
      console.warn("🟡 [setNotas] Array vazio — nada para salvar");
      cache.notas = novas;
      setNotasState(novas);
      return;
    }

    cache.notas = novas;
    setNotasState(novas);

    // Dedup ANTES de fatiar: a mesma NF repetida na planilha gera dois
    // registros com o id "nf-numero-serie-filial" e derruba o lote.
    const linhas = dedupPorId(novas.map(nfParaRow), "notas");
    const totalLotes = Math.ceil(linhas.length / 500);
    console.log(`🔵 [setNotas] Vai enviar ${linhas.length} notas em ${totalLotes} lote(s) de até 500`);

    for (let i = 0; i < linhas.length; i += 500) {
      const loteNum = Math.floor(i/500) + 1;
      const chunk = linhas.slice(i,i+500);

      console.log(`🔵 [setNotas] Lote ${loteNum}/${totalLotes} — enviando ${chunk.length} registros. Exemplo:`, chunk[0]);

      let resultado;
      try {
        resultado = await supabase.from("notas_fiscais").upsert(chunk, { onConflict:"id" });
      } catch(excecao) {
        console.error(`🔴 [setNotas] EXCEÇÃO JS no lote ${loteNum} (não é erro do Supabase, é erro de rede/código):`, excecao);
        setErroSalvar(`Exceção no lote ${loteNum}/${totalLotes}: ${excecao.message}`);
        throw excecao;
      }

      const { error, data, status, statusText } = resultado;
      console.log(`🔵 [setNotas] Lote ${loteNum} resposta — status:${status} ${statusText} | error:`, error, "| data:", data);

      if (error) {
        console.error(`🔴 [setNotas] ERRO DO SUPABASE no lote ${loteNum}:`, JSON.stringify(error, null, 2));
        setErroSalvar(`Lote ${loteNum}/${totalLotes} falhou: ${error.message} (code: ${error.code||"?"})`);
        throw error;
      }

      console.log(`🟢 [setNotas] Lote ${loteNum}/${totalLotes} confirmado com sucesso`);
      setProgresso(`Salvando notas: lote ${loteNum}/${totalLotes} (${Math.min(i+500,novas.length)}/${novas.length})...`);
    }

    console.log("🟢 [setNotas] TODOS os lotes salvos com sucesso!");
    setErroSalvar(null);
    setProgresso("");
  }, []);

  // ── Fretes auditados (valores realmente pagos) ──────────────────
  // Alimentados pela Auditoria de Fatura; usados para ratear o frete pago
  // por município no Mapa Operacional.
  const salvarAuditoriaFretes = useCallback(async (itens) => {
    if (!itens?.length) return 0;
    // Mesma proteção dos outros upserts: id repetido no lote derruba tudo com
    // "ON CONFLICT DO UPDATE command cannot affect row a second time".
    itens = dedupPorId(itens, "auditoria_fretes");
    for (let i = 0; i < itens.length; i += 500) {
      const { error } = await supabase.from("auditoria_fretes")
        .upsert(itens.slice(i, i + 500), { onConflict: "id" });
      if (error) {
        setErroSalvar("Erro ao salvar fretes auditados: " + error.message);
        throw error;
      }
    }
    // Recarrega o recorte local para o mapa refletir na hora
    await carregarAuditoriaFretes();
    setErroSalvar(null);
    return itens.length;
  }, []);

  // ── Atualização parcial: grava no banco SÓ as notas passadas (subset) e
  // mescla no estado, sem reescrever a base inteira. Usado no backfill de
  // município das notas importadas antes do cliente existir.
  // opts.apenasIndicadores = true envia só id + colunas de indicador. Use
  // quando a alteração for exclusivamente de prazo/previsão/OTIF — é o caso
  // do recálculo automático, que mexe em 5 colunas e mandava 27.
  const atualizarNotasParcial = useCallback(async (subset, opts = {}) => {
    if (!subset?.length) return 0;
    // Indexa por idNota (as notas em memória usam _id, não id)
    const byId = new Map((cache.notas || []).map(n => [idNota(n), n]));
    subset.forEach(n => byId.set(idNota(n), n));
    const full = Array.from(byId.values());
    cache.notas = full;
    setNotasState(full);

    const rowsFull = dedupPorId(subset.map(nfParaRow), "notas (parcial)");
    let enxuto = opts.apenasIndicadores === true;

    // ── Tamanho da requisição ────────────────────────────────────
    // O teste com 20.000 por requisição FALHOU com "TypeError: Failed to
    // fetch": 20k notas dão ~6MB de corpo e a requisição morre antes de
    // chegar ao Postgres. Não é erro do banco — é a rede recusando o envio.
    //
    // Duas lições ficaram no código:
    //   1. enviar() precisa capturar EXCEÇÃO, não só o { error } do Supabase.
    //      Um fetch que estoura LANÇA; a primeira versão do fallback só
    //      tratava erro retornado, então a exceção passava por cima dele e
    //      abortava a atualização inteira — foi o que o usuário viu na tela.
    //   2. Depois de uma falha, insistir no tamanho grande é jogar fora
    //      ~6MB e vários segundos por bloco. loteAtual degrada e NÃO volta
    //      a subir dentro da sessão.
    const LOTE_SEGURO = 500;

    let semInd = false, semAgd = false;

    // Aplica as degradações já detectadas nesta execução. A ordem não importa:
    // os dois conjuntos de colunas são disjuntos.
    const prep = lote => {
      let l = lote;
      if (semInd) l = l.map(semIndicadores);
      if (semAgd) l = l.map(semAgenda);
      return l;
    };

    // Envia um bloco; devolve o erro (ou a exceção convertida em erro) em vez
    // de lançar, para o chamador decidir entre repetir menor ou desistir.
    async function enviar(lote) {
      try {
        let { error } = await supabase.from("notas_fiscais")
          .upsert(prep(lote), { onConflict: "id" });

        // Colunas de agendamento ausentes: desiste delas e repete. Testado
        // ANTES do bloco de indicadores porque a mensagem do PostgREST cita o
        // nome da coluna, e o teste genérico por /column/ do outro ramo
        // engoliria este caso e removeria os indicadores por engano.
        if (error && !semAgd && COLS_AGENDA.some(c => (error.message || "").includes(c))) {
          console.warn("[TMS] Colunas de agendamento ausentes — rode a migration " +
                       "20260810000001_agendamento_entrega.sql. As entregas foram salvas; " +
                       "o agendamento não será persistido até a migration rodar.");
          semAgd = true;
          ({ error } = await supabase.from("notas_fiscais")
            .upsert(prep(lote), { onConflict: "id" }));
        }

        if (error && !semInd && /prazo_calc|previsao_iso|no_prazo|status_validacao|calc_em|column/i.test(error.message || "")) {
          console.warn("[TMS] Colunas de indicadores ausentes — rode a migration 20260723000003_notas_indicadores.sql");
          semInd = true;
          // Sem as colunas de indicador, o payload enxuto viraria só { id }:
          // desliga o modo e deixa o chamador repetir com a linha completa.
          enxuto = false;
          ({ error } = await supabase.from("notas_fiscais")
            .upsert(prep(lote), { onConflict: "id" }));
        }
        return error;
      } catch (ex) {
        // "Failed to fetch", timeout de rede, payload recusado…
        return ex instanceof Error ? ex : new Error(String(ex));
      }
    }

    const t0 = performance.now();
    let reqs = 0, caiuParaSeguro = false, caiuParaCompleto = false;
    const loteInicial = loteAtual;
    const enxutoInicial = enxuto;

    let i = 0;
    while (i < rowsFull.length) {
      const blocoFull = rowsFull.slice(i, i + loteAtual);
      const tb = performance.now();
      let error = await enviar(enxuto ? blocoFull.map(soIndicadores) : blocoFull);
      reqs++;

      // Payload enxuto recusado (linha inexistente, coluna obrigatória
      // faltando): refaz ESTE bloco completo e não tenta enxuto de novo.
      if (error && enxuto) {
        console.warn(`[notas parcial] payload enxuto recusado (${error.message}) — ` +
          `repetindo com a linha completa`);
        enxuto = false;
        caiuParaCompleto = true;
        error = await enviar(blocoFull);
        reqs++;
      }

      if (!error) {
        console.log(`[notas parcial] ${blocoFull.length.toLocaleString("pt-BR")} notas em ` +
          `${Math.round(performance.now() - tb)}ms${enxuto ? " (enxuto)" : ""}`);
        i += blocoFull.length;
        continue;
      }

      // Já estamos no menor tamanho: não há o que reduzir, é erro de verdade.
      if (loteAtual <= LOTE_SEGURO) {
        setErroSalvar("Erro ao atualizar notas: " + error.message);
        throw error;
      }

      // Degrada e REFAZ o mesmo trecho — i não avança, nada é pulado.
      const anterior = loteAtual;
      loteAtual = Math.max(LOTE_SEGURO, Math.floor(loteAtual / 4));
      caiuParaSeguro = true;
      console.warn(`[notas parcial] lote de ${anterior} falhou (${error.message}) — ` +
        `reduzindo para ${loteAtual} e refazendo o mesmo trecho`);
    }

    console.log(`⏱ [notas parcial] ${rowsFull.length.toLocaleString("pt-BR")} notas · ` +
      `${reqs} requisição(ões) · lote ${loteInicial}${caiuParaSeguro ? "→" + loteAtual : ""} · ` +
      `payload ${enxutoInicial ? (caiuParaCompleto ? "enxuto→completo" : "enxuto (6 colunas)") : "completo (27 colunas)"} · ` +
      `total: ${Math.round(performance.now() - t0)}ms`);

    setErroSalvar(null);
    return subset.length;
  }, []);

  // ── Controle de concorrência ─────────────────────────────────
  // Busca a versão atual do Supabase e mescla com as mudanças locais
  async function salvarTabelasSafe(novas, antigas) {
    // Buscar versão atual do Supabase
    const { data: atual, error } = await supabase
      .from("tabelas_frete").select("id,cod,updated_at").order("cod");
    if (error) throw error;

    // Detectar conflitos: itens que mudaram no Supabase desde que carregamos
    const mapaAtual = {};
    (atual||[]).forEach(r => mapaAtual[r.id] = r.updated_at);
    const mapaAntigo = {};
    (antigas||[]).forEach(r => mapaAntigo[r.id||r.cod] = r._updated_at);

    const conflitos = novas.filter(t => {
      const id = t.id || t.cod;
      return mapaAtual[id] && mapaAntigo[id] && mapaAtual[id] !== mapaAntigo[id];
    });

    if (conflitos.length > 0) {
      const nomes = conflitos.map(t => t.cod).join(", ");
      const ok = window.confirm(
        `⚠️ Conflito de edição!

As tabelas abaixo foram alteradas por outro usuário:
${nomes}

Deseja sobrescrever com sua versão?`
      );
      if (!ok) {
        // Recarregar do Supabase sem salvar
        const { data: fresh } = await supabase.from("tabelas_frete").select("*").order("cod");
        const freshed = (fresh||[]).map(rowParaTabela).map(t => ({ ...t, _updated_at: (fresh.find(r=>r.id===t.id)||{}).updated_at }));
        cache.tabelas = freshed;
        setTabelasState(freshed);
        return false; // cancelou
      }
    }

    // Garantir sequência de IDs para novos itens
    const idsExistentes = new Set((atual||[]).map(r => r.id));
    const novasComId = novas.map(t => {
      if (t.id && idsExistentes.has(t.id)) return t; // já existe
      if (t.id) return t; // novo com ID definido
      // Sem ID — não deve acontecer mas proteger
      return { ...t, id: t.cod };
    });

    return novasComId;
  }

  async function salvarMunicipiosSafe(novos, antigos) {
    const { data: atual, error } = await supabase
      .from("municipios").select("id,mun,updated_at");
    if (error) throw error;

    const mapaAtual = {};
    (atual||[]).forEach(r => mapaAtual[r.id] = r.updated_at);
    const mapaAntigo = {};
    (antigos||[]).forEach(r => mapaAntigo[r.id] = r._updated_at);

    const conflitos = novos.filter(m =>
      mapaAtual[m.id] && mapaAntigo[m.id] && mapaAtual[m.id] !== mapaAntigo[m.id]
    );

    if (conflitos.length > 0) {
      const nomes = conflitos.map(m => m.mun+"/"+m.uf).join(", ");
      const ok = window.confirm(
        `⚠️ Conflito de edição!

Municípios alterados por outro usuário:
${nomes}

Deseja sobrescrever?`
      );
      if (!ok) {
        const { data: fresh } = await supabase.from("municipios").select("*").order("mun");
        const freshed = (fresh||[]).map(rowParaMunicipio).map(m => ({ ...m, _updated_at: (fresh.find(r=>r.id===m.id)||{}).updated_at }));
        cache.municipios = freshed;
        setMunicipiosState(freshed);
        return false;
      }
    }
    return novos;
  }

  const setTabelas = useCallback(async (novasOuFn) => {
    const antigas = cache.tabelas || [];
    const novas = resolver(novasOuFn, antigas);

    // Check de concorrência antes de salvar
    const validadas = await salvarTabelasSafe(novas, antigas);
    // Retorna false para o chamador saber que NÃO salvou (usuário cancelou
    // por conflito) — antes retornava undefined e a tela fechava o editor
    // como se tivesse salvo, perdendo o que o usuário tinha acabado de fazer.
    if (!validadas) return false;

    // Usa um único timestamp para todos os rows — evita variação de milissegundos entre linhas
    const savedAt = new Date().toISOString();
    const rows = dedupPorId(validadas.map(t => ({
      id:t.id||t.cod, cod:t.cod, cod_transp:t.codTransp||"", transp:t.transp, tipo:t.tipo, modal:t.modal,
      vig_ini:t.vigIni, vig_fim:t.vigFim,
      adv:t.adv||0, adv_min:t.advMin||0, pgv:t.pgv||0, rkg:t.rkg||0, peso_acima:t.pesoAcima||0, rkg_acima:t.rkgAcima||0,
      minimo:t.minimo||0, gris:t.gris||0, gris_min:t.grisMin||0,
      desp:t.desp||0, tas:t.tas||0, tad:t.tad||0, ped:t.ped||0, ped_min:t.pedMin||0,
      rdc:t.rdc||0, rdc_tipo:t.rdcTipo||'percNota', icms:t.icms||0, excedente:t.excedente||0, frac:t.frac,
      rotas:t.rotas||[], faixas:t.faixas||[],
      updated_at: savedAt,
    })), "tabelas");
    const { error } = await supabase.from("tabelas_frete").upsert(rows,{onConflict:"id"});
    if (error) {
      console.error("Erro ao salvar tabelas:", error);
      setErroSalvar(`Falha ao salvar tabelas: ${error.message}`);
      throw error;
    }
    // Busca os updated_at REAIS que o Supabase armazenou (pode diferir se houver trigger no banco).
    // Sem isso, o próximo save detecta conflito falso porque o cache tem timestamp do cliente
    // enquanto o Supabase guardou o timestamp do servidor.
    const { data: tsReal } = await supabase
      .from("tabelas_frete").select("id,updated_at");
    const tsMap = {};
    (tsReal||[]).forEach(r => { tsMap[r.id] = r.updated_at; });
    const tabelasAtualizadas = validadas.map(t => ({
      ...t,
      _updated_at: tsMap[t.id||t.cod] || savedAt,
    }));
    cache.tabelas = tabelasAtualizadas;
    setTabelasState(tabelasAtualizadas);
    setErroSalvar(null);
    return true;
  }, []);

  const setMunicipios = useCallback(async (novosOuFn) => {
    const antigos = cache.municipios || [];
    const novos = resolver(novosOuFn, antigos);

    const validados = await salvarMunicipiosSafe(novos, antigos);
    if (!validados) return;

    const savedAtMun = new Date().toISOString();
    const rows = dedupPorId(validados.map(m => ({
      id:m.id||`${m.mun}-${m.uf}`,
      mun:m.mun, uf:m.uf, filial:m.filial, cod_ibge:m.codIBGE||"", filial_padrao:m.filialPadrao,
      transp_padrao:m.transpPadrao, prazo_padrao:m.prazoPadrao||null,
      transp_mec:m.transpMec, prazo_mec:m.prazoMec||null,
      transp_rural:m.transpRural, prazo_rural:m.prazoRural||null,
      transp_cwb:m.transpCWB, transp_mga:m.transpMGA, transp_cvl:m.transpCVL,
      transp_rbp:m.transpRBP, transp_mrl:m.transpMRL,
      // Campo vazio grava NULL, não 0: zero seria "entrega no mesmo dia".
      prazo_cwb: m.prazoCWB || null, prazo_mga: m.prazoMGA || null,
      prazo_cvl: m.prazoCVL || null, prazo_rbp: m.prazoRBP || null,
      prazo_mrl: m.prazoMRL || null,
      tem_tde:!!m.temTDE, taxas:m.taxas||[], obs:m.obs||"",
      updated_at: savedAtMun,
    })), "municípios");
    for (let i=0;i<rows.length;i+=500) {
      const { error } = await supabase.from("municipios").upsert(rows.slice(i,i+500),{onConflict:"id"});
      if (error) {
        console.error("Erro ao salvar municípios:", error);
        setErroSalvar(`Falha ao salvar municípios: ${error.message}`);
        throw error;
      }
    }
    // Busca timestamps reais do Supabase para evitar falso conflito no próximo save
    const { data: tsMunReal } = await supabase
      .from("municipios").select("id,updated_at");
    const tsMunMap = {};
    (tsMunReal||[]).forEach(r => { tsMunMap[r.id] = r.updated_at; });
    const municipiosAtualizados = validados.map(m => ({
      ...m,
      _updated_at: tsMunMap[m.id] || savedAtMun,
    }));
    cache.municipios = municipiosAtualizados;
    setMunicipiosState(municipiosAtualizados);
    setErroSalvar(null);
  }, []);

  const setOcorrencias = useCallback(async (novasOuFn) => {
    const novas = resolver(novasOuFn, cache.ocorrencias || []);
    cache.ocorrencias = novas; setOcorrenciasState(novas);
    const rows = dedupPorId(novas.map(ocorrenciaParaRow), "ocorrências");
    const { error } = await supabase.from("ocorrencias").upsert(rows,{onConflict:"id"});
    if (error) {
      console.error("Erro ao salvar ocorrências:", error);
      setErroSalvar(`Falha ao salvar ocorrências: ${error.message}`);
      throw error;
    }
    setErroSalvar(null);
  }, []);

  const setClientes = useCallback(async (novosOuFn) => {
    const novos = resolver(novosOuFn, cache.clientes || []);
    cache.clientes = novos; setClientesState(novos);
    const rows = dedupPorId(novos.map(clienteParaRow), "clientes");
    for (let i=0;i<rows.length;i+=500) {
      const { error } = await supabase.from("clientes").upsert(rows.slice(i,i+500),{onConflict:"id"});
      if (error) {
        console.error("Erro ao salvar clientes:", error);
        setErroSalvar(`Falha ao salvar clientes: ${error.message}`);
        throw error;
      }
    }
    setErroSalvar(null);
  }, []);

  // ── Setter de transportadoras com persistência no Supabase ─────────
  async function setTransportadoras(novasOuFn) {
    const antigas = cache.transportadoras || [];
    const novas = typeof novasOuFn === "function" ? novasOuFn(antigas) : novasOuFn;
    cache.transportadoras = novas;
    setTransportadorasState(novas);
    const rows = dedupPorId(novas.map(tr => ({
      id:    tr.id   || tr.cod,
      cod:   tr.cod,
      nome:  tr.nome,
      short: tr.short,
      filial:tr.filial || "",
      ativo: tr.ativo !== false,
      rastreio: tr.rastreio || null,
      sigla_ssw: (tr.siglaSsw || "").trim().toUpperCase() || null,
      cnpjs: [...new Set((tr.cnpjs || []).map(c => String(c).replace(/\D/g, "")).filter(c => c.length === 14))],
      frota_propria: tr.frotaPropria === true,
      cnpjs_via_lubpress: [...new Set((tr.cnpjsViaLubpress || []).map(c => String(c).replace(/[^0-9]/g, "")).filter(c => c.length === 14))],
    })), "transportadoras");
    const { error } = await supabase.from("transportadoras")
      .upsert(rows, { onConflict:"id" });
    if (error) {
      console.error("Erro ao salvar transportadora:", error);
      setErroSalvar("Falha ao salvar transportadora: " + error.message);
      throw error;
    }
    setErroSalvar(null);
  }

  async function deletarTransportadora(id) {
    const { error } = await supabase.from("transportadoras").delete().eq("id", id);
    if (error) {
      console.error("Erro ao excluir transportadora:", error);
      setErroSalvar("Falha ao excluir: " + error.message);
      throw error;
    }
    const novas = (cache.transportadoras || []).filter(t => t.id !== id);
    cache.transportadoras = novas;
    setTransportadorasState(novas);
    setErroSalvar(null);
  }

  // ── Gravação por registro ────────────────────────────────────────
  // Os setters acima recebem a lista inteira e reenviam tudo: editar a
  // observação de um cliente reenviava todos os clientes (#103). Isto grava
  // só o que mudou, como atualizarNotasParcial já fazia para notas, e
  // desfaz POR REGISTRO quando a gravação falha — o rollback do #101
  // devolvia a lista inteira e atropelava salvamento concorrente.
  //
  // A lista completa continua existindo para a importação, que é o caso em
  // que reenviar tudo é o certo.
  function criarAtualizadorParcial({ chave, setState, tabela, paraRow, rotulo }) {
    return async subset => {
      const lista = (subset || []).filter(Boolean);
      if (!lista.length) return 0;

      const anterior = cache[chave] || [];
      const antesPorId = new Map(anterior.map(r => [r.id, r]));
      const porId = new Map(anterior.map(r => [r.id, r]));
      lista.forEach(r => porId.set(r.id, r));
      const completa = Array.from(porId.values());
      cache[chave] = completa; setState(completa);

      const mexidos = new Set(lista.map(r => r.id));
      const rows = dedupPorId(lista.map(paraRow), `${rotulo} (parcial)`);

      for (let i = 0; i < rows.length; i += 500) {
        const { error } = await supabase.from(tabela)
          .upsert(rows.slice(i, i + 500), { onConflict:"id" });
        if (error) {
          // Desfaz só os registros deste envio: o que outra gravação mudou
          // no meio do caminho fica de pé.
          const revertida = completa
            .map(r => (mexidos.has(r.id) ? antesPorId.get(r.id) || null : r))
            .filter(Boolean);
          cache[chave] = revertida; setState(revertida);
          console.error(`Erro ao salvar ${rotulo}:`, error);
          setErroSalvar(`Falha ao salvar ${rotulo}: ${error.message}`);
          throw error;
        }
      }
      setErroSalvar(null);
      return rows.length;
    };
  }

  // Upsert não apaga linha removida da lista. Sem um DELETE de verdade,
  // excluir sumia da tela e a linha continuava no banco — voltava no F5
  // (#103). Tabelas, transportadoras, feriados e notas já tinham o seu.
  function criarExcluidor({ chave, setState, tabela, rotulo }) {
    return async id => {
      if (!id) return false;
      const anterior = cache[chave] || [];
      const restante = anterior.filter(r => r.id !== id);
      cache[chave] = restante; setState(restante);

      const { error } = await supabase.from(tabela).delete().eq("id", id);
      if (error) {
        cache[chave] = anterior; setState(anterior);
        console.error(`Erro ao excluir ${rotulo}:`, error);
        setErroSalvar(`Falha ao excluir ${rotulo}: ${error.message}`);
        throw error;
      }
      setErroSalvar(null);
      return true;
    };
  }

  const atualizarClientesParcial = criarAtualizadorParcial({
    chave:"clientes", setState:setClientesState, tabela:"clientes",
    paraRow:clienteParaRow, rotulo:"clientes",
  });
  const atualizarOcorrenciasParcial = criarAtualizadorParcial({
    chave:"ocorrencias", setState:setOcorrenciasState, tabela:"ocorrencias",
    paraRow:ocorrenciaParaRow, rotulo:"ocorrências",
  });
  const excluirCliente   = criarExcluidor({ chave:"clientes",    setState:setClientesState,    tabela:"clientes",    rotulo:"cliente" });
  const excluirOcorrencia = criarExcluidor({ chave:"ocorrencias", setState:setOcorrenciasState, tabela:"ocorrencias", rotulo:"ocorrência" });

  // Envolver todos os setters para nunca deixar erro silencioso
  // A chave do cache e o setState entram para o envelope conseguir desfazer
  // quando a gravação falha (#101).
  const setTabelasSafe     = comTratamentoErro(setTabelas,     setErroSalvar, setSalvando, "tabelas",     setTabelasState);
  const setMunicipiosSafe  = comTratamentoErro(setMunicipios,  setErroSalvar, setSalvando, "municipios",  setMunicipiosState);
  const setOcorrenciasSafe = comTratamentoErro(setOcorrencias, setErroSalvar, setSalvando, "ocorrencias", setOcorrenciasState);
  const setClientesSafe    = comTratamentoErro(setClientes,    setErroSalvar, setSalvando, "clientes",    setClientesState);
  const setNotasSafe       = comTratamentoErro(setNotas,       setErroSalvar, setSalvando, "notas",       setNotasState);

  // ── Delete real no Supabase ─────────────────────────────────────
  // setTabelas faz upsert (insere/atualiza) — não apaga linhas removidas.
  // Este helper executa o DELETE real e atualiza o cache/estado local.
  async function deletarTabela(id) {
    const { error } = await supabase.from("tabelas_frete").delete().eq("id", id);
    if (error) {
      console.error("Erro ao excluir tabela:", error);
      setErroSalvar(`Falha ao excluir tabela: ${error.message}`);
      throw error;
    }
    const novas = (cache.tabelas || []).filter(t => (t.id || t.cod) !== id);
    cache.tabelas = novas;
    setTabelasState(novas);
    setErroSalvar(null);
  }

  return {
    notas, setNotas: setNotasSafe,
    atualizarNotasParcial: comTratamentoErro(atualizarNotasParcial, setErroSalvar, setSalvando),
    auditoriaFretes,
    feriados, setFeriados, excluirFeriado,
    salvarAuditoriaFretes: comTratamentoErro(salvarAuditoriaFretes, setErroSalvar, setSalvando),
    auditorias, salvarAuditoria, excluirAuditoria,
    rastreioSsw,
    // A tela Notas lê de novo ao abrir: o robô grava de hora em hora (#363).
    recarregarRastreioSsw: carregarRastreioSsw,
    kpis, recarregarKpis: carregarKpis,
    garantirNotas, carregarPeriodo, notasDesde, garantirAuditoriaFretes,
    clientes, setClientes: setClientesSafe,
    // Caminho por registro: edição e exclusão de um cliente/ocorrência. A
    // lista inteira (setClientes/setOcorrencias) fica para a importação.
    atualizarClientesParcial: comTratamentoErro(atualizarClientesParcial, setErroSalvar, setSalvando),
    excluirCliente: comTratamentoErro(excluirCliente, setErroSalvar, setSalvando),
    atualizarOcorrenciasParcial: comTratamentoErro(atualizarOcorrenciasParcial, setErroSalvar, setSalvando),
    excluirOcorrencia: comTratamentoErro(excluirOcorrencia, setErroSalvar, setSalvando),
    tabelas, setTabelas: setTabelasSafe,
    deletarTabela,
    municipios, setMunicipios: setMunicipiosSafe,
    ocorrencias, setOcorrencias: setOcorrenciasSafe,
    transportadoras: transportadoras,
    setTransportadoras: comTratamentoErro(setTransportadoras, setErroSalvar, setSalvando),
    deletarTransportadora,
    // loading: agora bloqueia a UI até tabelas, municípios, notas E clientes
    // terminarem de carregar — antes só esperava tabelas/municípios (fase 1)
    // e deixava notas/clientes carregarem com a tela já liberada.
    // loading só bloqueia fase crítica (tabelas/municípios).
    // Notas e clientes carregam com banner de progresso sem travar a UI.
    loading: loadingCriticos,
    loadingNotas, loadingClientes,
    progresso, erro, erroSalvar, salvando,
    recarregar: () => {
      Object.keys(cache).forEach(k => cache[k] = null);
      iniciado.current = false;
      carregarFase1();
    },
    // Exclusão pontual de notas por id.
    //
    // Existe só para lixo de importação — linha de cabeçalho que entrou como
    // nota, registro com número não numérico. Nota fiscal legítima NÃO se
    // apaga: o registro fiscal e o cruzamento com o CT-e dependem dela
    // existir, e para tirar dos indicadores existe "desconsiderar".
    excluirNotas: async (ids) => {
      const lista = (ids || []).filter(Boolean);
      if (!lista.length) return { erro: null, removidas: 0 };
      // Em blocos: uma cláusula IN com milhares de ids estoura o limite de
      // tamanho da URL do PostgREST.
      for (let i = 0; i < lista.length; i += 200) {
        const { error } = await supabase.from("notas_fiscais")
          .delete().in("id", lista.slice(i, i + 200));
        if (error) return { erro: error.message, removidas: i };
      }
      cache.notas = null;
      setNotasState(prev => prev.filter(n => !lista.includes(n.id || n._id)));
      return { erro: null, removidas: lista.length };
    },
    // Recarrega só as notas sem precisar reimportar o Excel
    recarregarNotas: () => {
      cache.notas = null;
      setLoadingNotas(true);
      setErro(null);
      carregarNotas();
    },
  };
}
